import { HtmlCanvasVisual, IRenderContext, Rect } from '@ardoq/yfiles';
import { colors } from '@ardoq/design-tokens';
import { ArdoqIcon, ConditionalFormattingState } from '@ardoq/data-model';
import { LeafNodeVisualProperties } from './types';
import {
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH as NODE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_CORNER_RADIUS as CORNER_RADIUS,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_SHAPE_HEIGHT as SHAPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_CONTEXT_HIGHLIGHT_STROKE as CONTEXT_HIGHLIGHT_STROKE,
  MODERNIZED_BLOCK_DIAGRAM_CONTEXT_HIGHLIGHT_GAP as CONTEXT_HIGHLIGHT_GAP,
  MODERNIZED_BLOCK_DIAGRAM_SELECTION_STROKE_WIDTH as SELECTION_STROKE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_NODE_BORDER_WIDTH as NODE_BORDER_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_CONDITIONAL_FORMATTING_INSET as CONDITIONAL_FORMATTING_INSET,
  MODERNIZED_BLOCK_DIAGRAM_CONDITIONAL_FORMATTING_STROKE_WIDTH as CONDITIONAL_FORMATTING_STROKE_WIDTH,
} from '../consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_IMAGE_CIRCLE_STROKE_WIDTH as CIRCLE_STROKE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_ICON_SIZE as ICON_SIZE,
} from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { svgImage } from 'tabview/canvasRendering/canvasResolvedImages';
import { calculateIconLayout, calculateImageInCircleLayout } from './util';
import { determineStyleSize } from 'yfilesExtensions/styles/util';
import {
  paintIconRepresentation,
  paintImageRepresentation,
} from './paintRepresentation';

const CIRCLE_RADIUS = SHAPE_HEIGHT / 2;

type ShapeLayout = {
  type: 'shape';
  x: number;
  y: number;
  width: number;
  height: number;
};

type ImageLayout = {
  type: 'image';
  cx: number;
  cy: number;
  imageRadius: number;
  imageHref: string;
};

type IconLayout = {
  type: 'icon';
  cx: number;
  cy: number;
  circleRadius: number;
  icon: ArdoqIcon;
};

type LeafNodeLayout = ShapeLayout | ImageLayout | IconLayout | null;

const calculateLeafNodeLayout = (
  props: LeafNodeVisualProperties
): LeafNodeLayout => {
  const { x, y, shapeY, shapeTemplateId, imageHref, icon } = props;
  const cx = x + NODE_WIDTH / 2;
  const cy = y + shapeY + CIRCLE_RADIUS;

  if (shapeTemplateId) {
    const originalSize = determineStyleSize(`#${shapeTemplateId}`);
    const originalAspectRatio = originalSize.width / originalSize.height;

    // Calculate width based on maintaining aspect ratio within available height
    const shapeWidth = Math.min(SHAPE_HEIGHT * originalAspectRatio, NODE_WIDTH);
    const centeredX = x + (NODE_WIDTH - shapeWidth) / 2;

    return {
      type: 'shape',
      x: centeredX,
      y: y + shapeY,
      width: shapeWidth,
      height: SHAPE_HEIGHT,
    };
  } else if (imageHref) {
    const imageRadius = CIRCLE_RADIUS - CIRCLE_STROKE_WIDTH;

    return {
      type: 'image',
      cx,
      cy,
      imageRadius,
      imageHref,
    };
  } else if (icon) {
    return {
      type: 'icon',
      cx,
      cy,
      circleRadius: CIRCLE_RADIUS,
      icon,
    };
  }

  return null;
};

const layoutToRect = (
  layout: LeafNodeLayout,
  props: LeafNodeVisualProperties
): Rect => {
  const { x, y, shapeY } = props;

  if (!layout) {
    const defaultWidth = NODE_WIDTH / 2;
    const centeredX = x + (NODE_WIDTH - defaultWidth) / 2;
    return new Rect(centeredX, y + shapeY, defaultWidth, SHAPE_HEIGHT);
  }

  switch (layout.type) {
    case 'shape':
      return new Rect(layout.x, layout.y, layout.width, layout.height);
    case 'image':
      return calculateImageInCircleLayout(
        layout.cx,
        layout.cy,
        layout.imageRadius,
        layout.imageHref
      );
    case 'icon':
      return calculateIconLayout(layout.cx, layout.cy, ICON_SIZE, layout.icon);
  }
};

const renderLeafNodeImage = (
  ctx: CanvasRenderingContext2D,
  layout: ImageLayout,
  color: string
) => {
  paintImageRepresentation(
    ctx,
    layout.imageHref,
    layout.cx,
    layout.cy,
    CIRCLE_RADIUS,
    color
  );
};

const renderLeafNodeIcon = (
  ctx: CanvasRenderingContext2D,
  layout: IconLayout,
  color: string
) => {
  paintIconRepresentation(
    ctx,
    layout.cx,
    layout.cy,
    layout.circleRadius,
    color,
    layout.icon
  );
};

const renderLeafNodeShape = (
  ctx: CanvasRenderingContext2D,
  props: LeafNodeVisualProperties
) => {
  const { shapeTemplateId, color, x, y, shapeY } = props;
  if (!shapeTemplateId) return;

  // The calculated layout width is not used here, since it preserves the aspect ratio
  // based on the original shape height
  ctx.drawImage(
    svgImage(
      `${shapeTemplateId}~~${color}~~${color}~~${color}~~${NODE_WIDTH}~~${SHAPE_HEIGHT}`
    ),
    x,
    y + shapeY
  );
};

class LeafNodeVisual extends HtmlCanvasVisual {
  public static getLayout(props: LeafNodeVisualProperties): Rect {
    const layout = calculateLeafNodeLayout(props);
    return layoutToRect(layout, props);
  }
  private layout: LeafNodeLayout;

  constructor(private props: LeafNodeVisualProperties) {
    super();
    this.layout = calculateLeafNodeLayout(props);
  }

  public getLayout(): Rect {
    return layoutToRect(this.layout, this.props);
  }

  override render(_: IRenderContext, ctx: CanvasRenderingContext2D): void {
    const {
      height,
      color,
      x,
      y,
      isTransparentized,
      isContext,
      isSelected,
      conditionalFormattingState,
    } = this.props;

    const globalAlpha = ctx.globalAlpha;
    if (isTransparentized) {
      ctx.globalAlpha = 0.32;
    }

    if (isContext) {
      // #region context highlight
      const gap =
        CONTEXT_HIGHLIGHT_GAP + (isSelected ? SELECTION_STROKE_WIDTH : 0);
      const [highlightX, highlightY] = [x, y].map(
        p => p - gap - CONTEXT_HIGHLIGHT_STROKE / 2
      );
      const [highlightWidth, highlightHeight] = [NODE_WIDTH, height].map(
        l => l + 2 * gap + CONTEXT_HIGHLIGHT_STROKE
      );
      const highlightRadius =
        CORNER_RADIUS + gap + CONTEXT_HIGHLIGHT_STROKE / 2;

      ctx.beginPath();
      ctx.roundRect(
        highlightX,
        highlightY,
        highlightWidth,
        highlightHeight,
        highlightRadius
      );
      ctx.lineWidth = CONTEXT_HIGHLIGHT_STROKE;
      ctx.strokeStyle = colors.blue600;
      ctx.stroke();
      // #endregion
    }

    // #region border
    ctx.beginPath();
    ctx.roundRect(x, y, NODE_WIDTH, height, CORNER_RADIUS);
    ctx.lineWidth = NODE_BORDER_WIDTH;
    ctx.strokeStyle = colors.grey300;
    ctx.stroke();
    ctx.fillStyle = colors.white950;
    ctx.fill();
    // #endregion

    // #region conditional formatting border
    if (conditionalFormattingState === ConditionalFormattingState.FORMATTED) {
      const { lineWidth } = ctx;
      ctx.beginPath();
      ctx.roundRect(
        x + CONDITIONAL_FORMATTING_INSET,
        y + CONDITIONAL_FORMATTING_INSET,
        NODE_WIDTH - 2 * CONDITIONAL_FORMATTING_INSET,
        height - 2 * CONDITIONAL_FORMATTING_INSET,
        CORNER_RADIUS - CONDITIONAL_FORMATTING_INSET
      );
      ctx.lineWidth = CONDITIONAL_FORMATTING_STROKE_WIDTH;
      ctx.strokeStyle = color;
      ctx.stroke();
      ctx.lineWidth = lineWidth;
    }
    // #endregion

    if (this.layout) {
      switch (this.layout.type) {
        case 'shape':
          renderLeafNodeShape(ctx, this.props);
          break;
        case 'image':
          renderLeafNodeImage(ctx, this.layout, color);
          break;
        case 'icon':
          renderLeafNodeIcon(ctx, this.layout, color);
          break;
      }
    }

    ctx.globalAlpha = globalAlpha;
  }
}

export { LeafNodeVisual };
